// Kid onboarding — Step 1: Name. Inserts a kid row and routes the parent
// to the next sub-step (age). The boot resolver still reports NEEDS_KID
// until the diagnostic completes; sub-step ordering lives in kidOnboarding.ts.

import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAccount, useKids } from "@/lib/account";
import { useQueryClient } from "@tanstack/react-query";
import { kidStepProgress, nextKidStep } from "@/lib/kidOnboarding";
import { useOnboardingDraft } from "@/lib/onboardingDraft";
import { useAppState } from "@/App/AppStateContext.tsx";
import { checkKidName, displayKidName } from "@/App/sharedRowGuard.js";

export const Route = createFileRoute("/onboarding/kid/name")({
  head: () => ({ meta: [{ title: "PencilWise · Add your child" }] }),
  component: KidName,
});

// H-A4 shared step styles (H-0 tokens): terracotta-deep eyebrow (AA-safe on
// cream, unlike raw --primary at small sizes), Fraunces h1, AA-safe helper.
const EYEBROW = "text-xs font-bold uppercase tracking-[0.18em] text-terracotta-deep";
const CTA =
  "flex min-h-[52px] w-full scroll-mb-28 items-center justify-center rounded-2xl bg-primary px-6 text-base font-extrabold text-primary-foreground shadow-press transition-all active:translate-y-0.5 active:bg-primary-pressed active:shadow-none disabled:opacity-50";

function KidName() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: account } = useAccount();
  const { data: kids = [] } = useKids();
  const app = useAppState();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const { current, total } = kidStepProgress("name");
  const { setDraft } = useOnboardingDraft();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setErr(null);

    const cleaned = displayKidName(name);

    // MA-800: the database allows one kid per name per parent (students_parent_name_unique),
    // so a name already on this device for the SAME signed-in user is refused up front.
    // Lowercased + trimmed compare; nothing else is blocked.
    const verdict = checkKidName({
      children: app?.registry?.children ?? [],
      name: cleaned,
      userId: app?.user?.id ?? null,
    });
    if (!verdict.ok) {
      setErr(verdict.message ?? null);
      return;
    }
    setBusy(true);

    // Phase G3 Stage 4: pencilwise commits everything at handoff
    // via the gate-ladder bridge (handleOnboardingComplete reads
    // the draft singleton). Per-step supabase writes are skipped
    // (the mock would no-op them anyway); the storybook reference
    // path is preserved in git history. Phase H wires real per-
    // step persistence if needed.
    setDraft({ name: cleaned });
    navigate({
      to: nextKidStep({ name: cleaned })?.path ?? "/onboarding/kid/age",
      search: (prev) => prev,
    });
  };

  return (
    <div className="relative min-h-screen bg-cream paper-grain">
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-10">
        <header className="pointer-events-none animate-slide-up">
          <p className={EYEBROW}>
            Step {current} of {total}
          </p>
          <h1 className="mt-2 font-serif text-3xl font-bold tracking-tight">
            What's your child's name?
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            So we can cheer them on by name.
          </p>
        </header>
        <form onSubmit={submit} className="mt-6 flex flex-col gap-3">
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Type your child's name"
            className="min-h-[56px] rounded-2xl border-2 border-border bg-card px-5 text-lg font-semibold text-foreground shadow-soft placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none"
          />
          {err && <p role="alert" className="text-xs font-bold text-terracotta-deep">{err}</p>}
          <button disabled={busy || !name.trim()} className={CTA}>
            {busy ? "Saving…" : "Continue →"}
          </button>
        </form>
      </div>
    </div>
  );
}
